import NetxQueryClient, {
	type SuccessResponse,
} from '@netx/core-next/lib/queryClient';
export type {QueryError} from '@netx/core-next/lib/queryClient';
import type {IIdSelection} from '@netx/core-next/types';
import {
	type AttributeVocabularyValueFilter,
	getAttributeVocabularyValueDefaultFilters,
} from '../schema';

/**
 * `removeAttributeVocabularyValues` request params
 * (what is used to send to server)
 */
export interface RemoveAttributeVocabularyValuesRequestOptions {
	vocabularyId: number;
	selection: IIdSelection<number>;
	filters?: Partial<AttributeVocabularyValueFilter>;
}

/**
 * `removeAttributeVocabularyValues` request
 * (what gets sent to the server)
 */
export type RemoveAttributeVocabularyValuesRequest = [
	RemoveAttributeVocabularyValuesRequestOptions['vocabularyId'],
	RemoveAttributeVocabularyValuesRequestOptions['selection'],
	AttributeVocabularyValueFilter['value'],
];

/**
 * `removeAttributeVocabularyValues` response
 * (what comes back from the server)
 */
export type RemoveAttributeVocabularyValuesResponse =
	SuccessResponse<RemoveAttributeVocabularyValuesResult>;

/**
 * `removeAttributeVocabularyValues` result
 * (what is utilized from the server response)
 */
export type RemoveAttributeVocabularyValuesResult = number[];

export const removeAttributeVocabularyValuesEndpoint =
	'removeAttributeVocabularyValues';

/**
 * Bulk delete groups
 * @param params Params
 * @returns Promise of group ids that were deleted
 *
 * @example
 * ```ts
 * const deletedIds = await removeAttributeVocabularyValues({
 *  vocabularyId: 1,
 * 	selection: {
 * 		all: true,
 * 		excludeIds: [1, 2, 3],
 * 		includeIds: [],
 * 	},
 * 	filters: {
 * 		value: 'lorem ipsum',
 * 	},
 * });
 * ```
 */
export const removeAttributeVocabularyValues = async ({
	selection,
	filters,
	vocabularyId,
}: RemoveAttributeVocabularyValuesRequestOptions): Promise<RemoveAttributeVocabularyValuesResult> => {
	const client = NetxQueryClient();

	const requestParams: RemoveAttributeVocabularyValuesRequest = [
		vocabularyId,
		selection,
		getAttributeVocabularyValueDefaultFilters({filters}).value,
	];

	return client
		.post<RemoveAttributeVocabularyValuesResponse>(
			removeAttributeVocabularyValuesEndpoint,
			requestParams,
		)
		.then((response) => response.data.result);
};
